import { useNavigate } from 'react-router-dom';
import { Stack, Group, Text, Button, Badge } from '@repletoryreach/sdk-ui';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { Alert, StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import type { admin } from '@repletoryreach/sdk-api';
import { CopyableId } from '../../../components/CopyableId';
import { TenantName } from '../../../components/TenantName';
import { formatDateTime, formatInteger, formatRelative } from '../../../lib/format';
import { stateOf, STATE_LABEL_KEYS, STATE_TONE } from '../state';
import { useT } from '../../../i18n/I18nProvider';
import { whatsAppPreview } from '@repletoryreach/sdk-utils/whatsapp-text';

type Conversation = admin.AdminConversation;

/**
 * The triage surface for one conversation.
 *
 * A row used to navigate straight to the tenant page, which lost every
 * identifier a support engineer needs to paste into a ticket. The list now
 * reads by contact and tenant and the ids live here, behind copy actions,
 * with the tenant jump kept as an action rather than as the row's only
 * behaviour.
 */
export function ConversationDrawer({ row, onClose }: { row: Conversation | null; onClose: () => void }) {
  const t = useT();
  const nav = useNavigate();

  return (
    <Drawer opened={row !== null} onClose={onClose} title={t('conversations.drawer.title')} size="md">
      {row && (
        <Stack gap="lg">
          <Stack gap="xs">
            <Text role="subtitle" fw={600}>{row.contact_display_name ?? row.contact_msisdn}</Text>
            <CopyableId value={row.contact_msisdn} label={row.contact_msisdn} chars={20} />
          </Stack>

          <Stack gap="md">
            <Field label={t('conversations.drawer.state')}>
              {(() => {
                const state = stateOf(row);
                return state
                  ? <StatusBadge status={STATE_TONE[state]}>{t(STATE_LABEL_KEYS[state])}</StatusBadge>
                  : <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('common.notAvailable')}</Text>;
              })()}
            </Field>
            <Field label={t('conversations.drawer.tenant')}>
              <TenantName id={row.organisation_id} withCopy />
            </Field>
            <Field label={t('conversations.drawer.agent')}>
              {row.assigned_agent_user_id
                ? (
                  <Group gap="sm" wrap="nowrap">
                    <Text role="bodySmall">{t('conversations.assigned')}</Text>
                    <CopyableId value={row.assigned_agent_user_id} label={t('conversations.copy.agentId')} />
                  </Group>
                )
                : <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('conversations.unassigned')}</Text>}
            </Field>
            <Field label={t('conversations.drawer.unread')}>
              {row.unread_count > 0
                ? <Badge size="sm" color="orange" variant="light">{formatInteger(row.unread_count)}</Badge>
                : <Text role="bodySmall" c="var(--rr-color-text-muted)">{formatInteger(0)}</Text>}
            </Field>
            <Field label={t('conversations.drawer.opened')}>
              <Text role="bodySmall">{formatDateTime(row.created_at)}</Text>
            </Field>
            <Field label={t('common.updated')}>
              <span title={formatDateTime(row.last_message_at)}>
                <Text role="bodySmall">{formatRelative(row.last_message_at)}</Text>
              </span>
            </Field>
            <Field label={t('conversations.drawer.conversationId')}>
              <CopyableId value={row.id} label={t('conversations.copy.conversationId')} />
            </Field>
            <Field label={t('conversations.drawer.phoneNumberId')}>
              <CopyableId value={row.phone_number_id} label={t('conversations.copy.phoneNumberId')} />
            </Field>
          </Stack>

          <Stack gap="sm">
            <Text role="caption" c="var(--rr-color-text-muted)">{t('conversations.drawer.lastMessage')}</Text>
            <Text role="bodySmall" style={{ whiteSpace: 'pre-wrap' }}>
              {row.last_message_preview ? whatsAppPreview(row.last_message_preview) : t('conversations.noPreview')}
            </Text>
          </Stack>

          <Alert color="info" variant="light">{t('conversations.drawer.hint')}</Alert>

          <Group gap="sm">
            <Button size="xs" onClick={() => nav(`/tenants/${row.organisation_id}`)}>
              {t('conversations.drawer.openTenant')}
            </Button>
            <Button size="xs" variant="subtle" onClick={onClose}>{t('action.close')}</Button>
          </Group>
        </Stack>
      )}
    </Drawer>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Stack gap="xs">
      <Text role="caption" c="var(--rr-color-text-muted)">{label}</Text>
      {children}
    </Stack>
  );
}
